import React from 'react'
import { PredictedDish } from '../../../../types/menuTypes'
import {
  DishCard,
  DishCategory,
  DishDescription,
  DishListContainer,
  DishName,
  DishPrice,
} from '../styled/MenuScanResults.styled'
import ImpressionTracker from '../../../../components/analytics/ImpressionTracker'
import { logEvent } from '../../../../analytics/analytics'

interface MenuScanDishListProps {
  dishes: PredictedDish[]
  scanId?: string
  /** When false (e.g. homepage scan), use neutral copy instead of "this restaurant". */
  hasRestaurantContext?: boolean
  restaurantId?: string
}

const MenuScanDishList: React.FC<MenuScanDishListProps> = ({
  dishes,
  scanId,
  hasRestaurantContext = true,
  restaurantId,
}) => {
  const generateDescription = (dish: PredictedDish): string => {
    // Generate a neutral, factual description based on available data
    const dishData = dish.dish
    const parts: string[] = []

    if (dishData.description && dishData.description.trim()) {
      return dishData.description
    }

    // Fallback: construct from available information
    if (dishData.ingredients && dishData.ingredients.trim()) {
      parts.push(`Made with ${dishData.ingredients.toLowerCase()}`)
    }

    if (dishData.preparation && dishData.preparation.trim()) {
      parts.push(`prepared ${dishData.preparation.toLowerCase()}`)
    }

    if (dishData.dietary && dishData.dietary.trim()) {
      parts.push(`suitable for ${dishData.dietary.toLowerCase()} diets`)
    }

    if (parts.length > 0) {
      return parts.join(', ') + '.'
    }

    // Final fallback for completely empty data
    const category = dish.probability?.matchLabel?.toLowerCase() ?? 'dish'
    return hasRestaurantContext
      ? `A ${category} available at this restaurant.`
      : `A ${category} from the menu.`
  }

  return (
    <DishListContainer>
      {dishes.map((predictedDish, index) => (
        // dish_id uses synthetic scan-${i} ids (see MenuScanResults.tsx mapApiDishesToPredicted).
        // Emit for funnel/engagement; filter dish_id LIKE 'scan-%' in ML queries to exclude from catalog joins.
        <ImpressionTracker
          key={predictedDish.dish.dishID || index}
          onImpress={() =>
            logEvent('dish_impression', {
              restaurant_id: restaurantId ?? '',
              dish_id: predictedDish.dish.dishID,
              rank_position: index,
              screen_name: 'menu_scan_results',
              // Collection layer for inference-first: scan_id joins this engagement back to the
              // persisted menu_scan_session; dish_name carries the taste signal (attributes aren't
              // available client-side on scanned dishes). No inference applied yet — capture only.
              scan_id: scanId ?? '',
              dish_name: predictedDish.dish.name,
            })
          }
        >
          <DishCard
            onClick={() =>
              logEvent('dish_click', {
                restaurant_id: restaurantId ?? '',
                dish_id: predictedDish.dish.dishID,
                rank_position: index,
                screen_name: 'menu_scan_results',
                scan_id: scanId ?? '',
                dish_name: predictedDish.dish.name,
              })
            }
          >
            <DishName>
              {predictedDish.dish.name}
            </DishName>
            <DishCategory>
              {predictedDish.probability?.matchLabel ?? 'Main Course'}
            </DishCategory>
            <DishPrice>
              {predictedDish.dish.price}
            </DishPrice>
            <DishDescription>
              {generateDescription(predictedDish)}
            </DishDescription>
          </DishCard>
        </ImpressionTracker>
      ))}
    </DishListContainer>
  )
}

export default MenuScanDishList
